import clsx from 'clsx'
import { SearchMenuStates } from 'components/Search/type'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import type { AirportTransferJourneyType } from 'contexts/GlobalSearch/GlobalSearchState'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { rem } from 'polished'
import { useCallback, useContext, useRef } from 'react'
import type { MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const JourneyType = styled.div`
  align-self: center;
`

const JourneyTypeButton = styled(TextButton)`
  min-width: ${rem(112)};
  &.isOpen {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
    color: ${props => props.theme.palette.brand.primary.hover};
  }
`

const JourneyTypeOption = styled(ClickableListItem)`
  &.selected {
    background: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
  }

  &:hover {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
`

interface Props {
  onJourneyTypeSelect: (journeyType: AirportTransferJourneyType) => void
  onJourneyTypeSelectionClose: () => void
  onJourneyTypeSelectionOpen: () => void
}

function CarsAirportTransferJourneyTypeSelector(props: Props) {
  const {
    onJourneyTypeSelect,
    onJourneyTypeSelectionClose,
    onJourneyTypeSelectionOpen,
  } = props

  const intl = useIntl()
  const { activeMenu, airportTransferJourneyType } = useContext(GlobalSearchStateContext)
  const isOpen = activeMenu === SearchMenuStates.AirportTransferJourneyTypeSelection
  const journeyTypeButtonRef = useRef(null)

  const selectJourneyType = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    onJourneyTypeSelect(event.currentTarget.value as AirportTransferJourneyType)
  }, [onJourneyTypeSelect])

  let journeyTypeLabel = intl.formatMessage({
    id: 'aLOgBGDdGKwYblXOQRpT',
    defaultMessage: 'Return',
    description: 'Whole round-trip airport transfer journey including both airport-to-hotel and hotel-to-airport rides. Return means both directions, not the return leg alone.',
  })

  if (airportTransferJourneyType === 'one-way') {
    journeyTypeLabel = intl.formatMessage({
      id: '0MUI2WELMhyKvXfEBPtV',
      defaultMessage: 'One-way',
      description: 'One-way journey option for airport transfer search',
    })
  }

  return (
    <JourneyType>
      <JourneyTypeButton
        kind="primary"
        variant="ghost"
        size="medium"
        ref={journeyTypeButtonRef}
        aria-expanded={isOpen}
        onClick={onJourneyTypeSelectionOpen}
        endIcon={<LineAngleDownIcon size="XS" />}
        className={clsx({ isOpen })}
      >
        {journeyTypeLabel}
      </JourneyTypeButton>
      <DropdownSheet
        size="S"
        open={isOpen}
        anchorRef={journeyTypeButtonRef}
        triggerRef={journeyTypeButtonRef}
        onClose={onJourneyTypeSelectionClose}
        portaled
        placement="bottom-start"
        noPaddingOnBody
        autoHeight
      >
        <Sheet padding="8 0">
          <Group gap={0} direction="vertical">
            <JourneyTypeOption
              title={intl.formatMessage({
                id: 'aLOgBGDdGKwYblXOQRpT',
                defaultMessage: 'Return',
                description: 'Whole round-trip airport transfer journey including both airport-to-hotel and hotel-to-airport rides. Return means both directions, not the return leg alone.',
              })}
              value="return"
              className={clsx({ selected: airportTransferJourneyType === 'return' })}
              onClick={selectJourneyType}
              size="medium"
            />
            <JourneyTypeOption
              title={intl.formatMessage({
                id: '0MUI2WELMhyKvXfEBPtV',
                defaultMessage: 'One-way',
                description: 'One-way journey option for airport transfer search',
              })}
              value="one-way"
              className={clsx({ selected: airportTransferJourneyType === 'one-way' })}
              onClick={selectJourneyType}
              size="medium"
            />
          </Group>
        </Sheet>
      </DropdownSheet>
    </JourneyType>
  )
}

export default CarsAirportTransferJourneyTypeSelector
